import { SearchIcon } from "components/icons/icn-index.icon";
import type { FiltersLoadCapacityType, FiltersUnloadCapacityType } from "pages/master-data/unit-management/grid-parameterization/types";
import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import type { IOption } from "types/option";
import { validatesArrayHasValue } from "utils/custom-functions";
import { useGridParameterizationContext } from "../../../../context";
import { List, ListItem, SearchInput, WarningMessage } from "./styled";

interface SelectableAndSearchableListProps {
  type: string;
  prop: string;
  data: IOption[];
  filters: FiltersLoadCapacityType | FiltersUnloadCapacityType | null;
}

const SelectableAndSearchableList = ({
  type,
  prop,
  data,
  filters,
}: SelectableAndSearchableListProps) => {
  const { handleChangeFilter } = useGridParameterizationContext();
  const [search, setSearch] = useState<string>("");
  const [listContent, setListContent] = useState(data);

  const handleChangeSearchInput = (value: string) => {
    setSearch(value);
  };

  const normalizeText = useCallback((text: string) => {
    if (!text) return "";
    return text
      .toString()
      .normalize("NFD") // Decompõe caracteres Unicode (ex: 'á' vira 'a' + '´')
      .replace(/[\u0300-\u036f]/g, "") // Remove apenas os acentos (diacríticos)
      .toLowerCase()
      .trim();
  }, []);

  useEffect(() => {
    if (search.length > 0) {
      const normalizedText = normalizeText(search);
      const result = data.filter((x) => normalizeText(x.Description).includes(normalizedText));
      setListContent(result);
    } else {
      setListContent(data);
    }
  }, [search, data, normalizeText]);

  return (
    <>
      {!validatesArrayHasValue(data) && !filters?.businessUnitId && 
        <WarningMessage>{intl.get("feedbacks.messageMandatoryBusinessUnit")}</WarningMessage>
      }
      {!validatesArrayHasValue(data) && filters?.businessUnitId && 
        <WarningMessage>{intl.get("commons.noRecordsFound")}</WarningMessage>
      }
      {validatesArrayHasValue(data) && (
        <>
          <SearchInput>
            <SearchIcon width={"44px"} height={undefined} color={undefined} />
            <input
              value={search}
              onChange={(ev) => handleChangeSearchInput(ev.currentTarget.value)}
              placeholder={intl.get("filters.search")}
              id="input-list-search"
            />
          </SearchInput>
          <List>
            {listContent.map((item, index) => (
              <ListItem
                key={`${item.Id}-${index}`}
                onClick={() => handleChangeFilter(type, prop, item.Id)}
                isSelected={item.Id === filters?.[prop as keyof typeof filters]}
                title={item.Description}
              >
                {item.Description}
              </ListItem>
            ))}
          </List>
        </>
      )}
    </>
  );
};

export default SelectableAndSearchableList;